/**
 * Maintenance windows - planned quiet, without lying about the board.
 *
 * The panel says out loud what the feature does and does not do, because the
 * obvious assumption is wrong in an important way: a window stops the EMAIL
 * and the TICKET, and changes nothing on the dashboard. Somebody who believed
 * otherwise would set a window and then be surprised by a wall of red on
 * Monday morning - or worse, trust that the red was suppressed too.
 *
 * The second thing it says out loud is that nothing is lost. "Held" is the
 * word used throughout rather than "suppressed" or "ignored", because a
 * machine still broken when the window closes still raises its alert.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { TimeField } from './TimeField';
import { minutesToHhmm } from './timeMath';

type Kind = 'once' | 'weekly';

interface Window_ {
  id: string;
  label: string;
  kind: Kind;
  startsAt?: number | null;
  endsAt?: number | null;
  days?: number[];
  startMinute?: number;
  endMinute?: number;
  clientIds: string[];
  targetIds: string[];
  enabled: boolean;
}

interface Held {
  targetId: string;
  windowLabel: string;
  releaseAt: number;
  transition: { name: string; client: string; to: string };
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Was a local copy of the same two functions the policies page had. They live
// in timeMath now, where they are tested - two copies of a time parser is two
// chances to disagree about what an empty string means.
const hhmm = minutesToHhmm;

/** For a datetime-local input, which wants local wall-clock with no zone. */
const toLocalInput = (ms: number) => {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function describe(w: Window_): string {
  if (w.kind === 'once') {
    if (!w.startsAt || !w.endsAt) return 'incomplete';
    const a = new Date(w.startsAt);
    const b = new Date(w.endsAt);
    const sameDay = a.toDateString() === b.toDateString();
    return sameDay
      ? `${a.toLocaleDateString()} ${a.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} to ${b.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : `${a.toLocaleString()} to ${b.toLocaleString()}`;
  }
  const days = (w.days ?? []).slice().sort().map((d) => DAYS[d]).join(', ') || 'no days';
  const overnight = (w.endMinute ?? 0) <= (w.startMinute ?? 0);
  return `${days} ${hhmm(w.startMinute ?? 0)} to ${hhmm(w.endMinute ?? 0)}${overnight ? ' (overnight)' : ''}`;
}

const blank = (): Window_ => ({
  id: '', label: '', kind: 'weekly',
  days: [0], startMinute: 2 * 60, endMinute: 6 * 60,
  startsAt: Date.now(), endsAt: Date.now() + 2 * 3_600_000,
  clientIds: [], targetIds: [], enabled: true,
});

export function MaintenanceWindows({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [windows, setWindows] = useState<Window_[]>([]);
  const [held, setHeld] = useState<Held[]>([]);
  const [draft, setDraft] = useState<Window_ | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    if (!orgId) return;
    try {
      const r = await httpsCallable(functions, 'listMaintenanceWindows')({ orgId });
      const d = r.data as { windows: Window_[]; held: Held[] };
      setWindows(d.windows ?? []);
      setHeld(d.held ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, [orgId]);

  async function save() {
    if (!draft || !orgId) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await httpsCallable(functions, 'saveMaintenanceWindow')({ orgId, ...draft });
      setDraft(null);
      setMsg('Window saved.');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function remove(id: string) {
    if (!orgId) return;
    setBusy(true); setErr(null);
    try {
      await httpsCallable(functions, 'deleteMaintenanceWindow')({ orgId, id });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  if (!orgId) return null;

  return (
    <section className="panel">
      <h2 className="panel-title">Maintenance windows</h2>
      <p className="panel-note" style={{ marginTop: 0 }}>
        During a window, failures do not send an email or open a ticket. They still appear on the
        board, with their reason, exactly as they would otherwise &mdash; a window makes the alerting
        quiet, not the dashboard wrong.
      </p>
      <p className="panel-note">
        <strong>Nothing is lost.</strong> A machine that is still failing when the window closes
        raises its alert then. One that recovered during the window &mdash; the ordinary result of a
        planned reboot &mdash; is dropped silently, which is the point.
      </p>

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner">{msg}</div>}

      <table className="mini-table">
        <thead><tr><th>Window</th><th>When</th><th>Scope</th><th /></tr></thead>
        <tbody>
          {windows.length === 0 && (
            <tr><td colSpan={4} className="dim">
              No windows. Every failure alerts immediately, which is the right default.
            </td></tr>
          )}
          {windows.map((w) => (
            <tr key={w.id}>
              <td>
                <div style={{ fontWeight: 600 }}>{w.label}</div>
                {!w.enabled && <span className="tag">paused</span>}
              </td>
              <td className="mono" style={{ fontSize: 12 }}>{describe(w)}</td>
              <td className="dim" style={{ fontSize: 12 }}>
                {w.clientIds.length === 0 && w.targetIds.length === 0
                  ? 'whole organisation'
                  : `${w.clientIds.length} client(s), ${w.targetIds.length} machine(s)`}
              </td>
              <td style={{ textAlign: 'right' }}>
                {canManage && (
                  <>
                    <button className="btn-link" onClick={() => setDraft(w)}>edit</button>
                    <button className="btn-link danger" style={{ marginLeft: 10 }}
                            disabled={busy} onClick={() => void remove(w.id)}>delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {held.length > 0 && (
        <>
          <h3 className="panel-title" style={{ fontSize: 13, marginTop: 18 }}>
            Held right now ({held.length})
          </h3>
          <p className="panel-note" style={{ marginTop: 0 }}>
            Shown so a window is never a black box. Each of these either alerts when its window
            closes, or is dropped because the machine recovered.
          </p>
          <table className="mini-table">
            <thead><tr><th>Machine</th><th>Client</th><th>Status</th><th>Releases</th></tr></thead>
            <tbody>
              {held.map((h) => (
                <tr key={h.targetId}>
                  <td>{h.transition?.name ?? h.targetId}</td>
                  <td className="dim">{h.transition?.client ?? ''}</td>
                  <td><span className="tag">{h.transition?.to ?? ''}</span></td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {new Date(h.releaseAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {canManage && !draft && (
        <button className="btn" style={{ marginTop: 12 }} onClick={() => setDraft(blank())}>
          Add a window
        </button>
      )}

      {draft && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="field">
              <span className="field-label">Name</span>
              <input className="field-input" value={draft.label} placeholder="Patch night"
                     onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">Repeats</span>
              <select className="field-input" value={draft.kind}
                      onChange={(e) => setDraft({ ...draft, kind: e.target.value as Kind })}>
                <option value="weekly">Every week</option>
                <option value="once">Once</option>
              </select>
            </label>
          </div>

          {draft.kind === 'weekly' ? (
            <div style={{ marginTop: 10 }}>
              <div className="field-label" style={{ marginBottom: 4 }}>Days</div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {DAYS.map((d, i) => {
                  const on = (draft.days ?? []).includes(i);
                  return (
                    <button
                      key={d} type="button"
                      className={'btn' + (on ? ' btn-primary' : '')}
                      onClick={() => {
                        const set = new Set(draft.days ?? []);
                        if (on) set.delete(i); else set.add(i);
                        setDraft({ ...draft, days: [...set] });
                      }}
                    >{d}</button>
                  );
                })}
              </div>
              <div className="row" style={{ gap: 10, marginTop: 10 }}>
                {/* The clock pops up on click and leaves once the minutes are
                    set. A window opening and closing is a time of day, which is
                    the one thing a clock face genuinely fits. */}
                <label className="field">
                  <span className="field-label">From</span>
                  <TimeField
                    label="Window opens"
                    value={draft.startMinute ?? 0}
                    onChange={(v) => setDraft({ ...draft, startMinute: v ?? 0 })}
                  />
                </label>
                <label className="field">
                  <span className="field-label">To</span>
                  <TimeField
                    label="Window closes"
                    value={draft.endMinute ?? 0}
                    onChange={(v) => setDraft({ ...draft, endMinute: v ?? 0 })}
                  />
                </label>
              </div>
              <p className="panel-note">
                An end earlier than the start means overnight, which is usually what a patch window
                is. Read in your organisation&rsquo;s time zone, set on the Policies page.
              </p>
            </div>
          ) : (
            <div className="row" style={{ gap: 10, marginTop: 10 }}>
              <label className="field">
                <span className="field-label">Starts</span>
                <input className="field-input" type="datetime-local"
                       value={toLocalInput(draft.startsAt ?? Date.now())}
                       onChange={(e) => setDraft({ ...draft, startsAt: new Date(e.target.value).getTime() })} />
              </label>
              <label className="field">
                <span className="field-label">Ends</span>
                <input className="field-input" type="datetime-local"
                       value={toLocalInput(draft.endsAt ?? Date.now())}
                       onChange={(e) => setDraft({ ...draft, endsAt: new Date(e.target.value).getTime() })} />
              </label>
            </div>
          )}

          <label className="row" style={{ gap: 8, marginTop: 12, alignItems: 'center' }}>
            <input type="checkbox" checked={draft.enabled}
                   onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
            <span>Active</span>
          </label>

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving' : 'Save window'}
            </button>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
